import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { enhancedApi as api } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { 
  CheckCircle, 
  XCircle, 
  Edit, 
  FileText, 
  User,
  Calendar,
  MessageSquare,
  Send,
  Save,
  Shield,
  Activity,
  ClipboardCheck,
  Award,
  Pencil,
  X,
  Check,
  Download
} from 'lucide-react';

interface AuditAssignment {
  id: number;
  case_type: string;
  case_id: number;
  assignment_type: string;
  multi_auditor_support: number;
  assigned_to_id: number;
  primary_auditor_id: number | null;
  reviewer_id: number;
  assigned_by: number;
  assigned_date: string;
  due_date: string;
  status: string;
  notes: string;
  priority: string;
  revision_count: number | null;
  last_revision_date: string | null;
  first_name: string;
  last_name: string;
  auditor_name?: string;
  // Additional fields from joins
  mortality_patient_id?: string;
  time_of_death?: string;
  cause_of_death?: string;
  final_diagnosis?: string;
  morbidity_patient_id?: string;
  incident_date?: string;
  case_summary?: string;
}

interface Question {
  id: string;
  question: string;
  answer: string;
  comment: string;
}

interface CaseInformation {
  patient_ip_number?: string;
  age?: string;
  admission_date?: string;
  discharge_date?: string;
  length_of_stay?: number;
  cause_of_death?: string;
  source_of_admission?: string;
  primary_diagnosis?: string;
  previous_admission_30_days?: number;
  dnr_status?: string;
  autopsy_needed?: number;
  autopsy_justification?: string;
  case_summary?: string;
  rca_needed?: number;
  rca_comment?: string;
  cpr_concern?: number;
  cpr_comment?: string;
  mortality_unexpected?: number;
  mortality_unexpected_comment?: string;
  mortality_not_expected_admission?: number;
  mortality_not_expected_admission_comment?: string;
}

interface FormData {
  case_information?: CaseInformation;
  documentation_questions?: Question[];
  diagnosis_questions?: Question[];
  management_questions?: Question[];
  outcome_questions?: Question[];
  disclosure_questions?: Question[];
  second_victim_questions?: Question[];
  issues_revealed?: string;
  recommendations?: any[];
  audit_team?: any[];
  progress_percentage?: number;
}

interface PatientData {
  id: number;
  patient_id: string;
  note_no: string;
  time_of_death: string;
  cause_of_death: string;
  final_diagnosis: string;
  dept: string;
  unit_id: string;
}

export const SupervisorReview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [audit, setAudit] = useState<AuditAssignment | null>(null);
  const [patientData, setPatientData] = useState<PatientData | null>(null);
  const [formData, setFormData] = useState<FormData>({});
  const [loading, setLoading] = useState(true);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewStatus, setReviewStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [systemSettings, setSystemSettings] = useState<any>({});
  const [userRole, setUserRole] = useState<string>('');

  useEffect(() => {
    if (id) {
      fetchAuditData();
      fetchSystemSettings();
      fetchUserRole();
    }
  }, [id]);

  const fetchAuditData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.reviews.getReview(parseInt(id!));
      if (response.success && 'data' in response) {
        setAudit(response.data.audit);
        setPatientData(response.data.patient);
        setFormData(response.data.form_data || {});
        // Debug: Log the form data structure
        console.log('Form data structure:', response.data.form_data);
        // Review notes and status will be loaded separately or set to empty initially
        setReviewNotes('');
        setReviewStatus('');
      } else {
        // Handle API error response
        setError(response.message || 'Failed to load audit data');
      }
    } catch (error: any) {
      console.error('Failed to fetch audit data:', error);
      setError(error.response?.data?.message || error.message || 'Failed to load audit data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemSettings = async () => {
    try {
      const response = await fetch('/api/admin.php?action=system_settings');
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSystemSettings(data.data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch system settings:', error);
    }
  };

  const fetchUserRole = async () => {
    try {
      const response = await fetch('/api/auth.php?action=get_user_info');
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.user) {
          setUserRole(data.user.role);
        }
      }
    } catch (error) {
      console.error('Failed to fetch user role:', error);
    }
  };

  // Check if editing is allowed for approved audits
  const canEditApprovedAudit = () => {
    if (audit?.status !== 'approved') return true;
    
    const allowEditApproved = systemSettings.allow_edit_approved_audits?.value === 'true';
    const hasEditPermission = userRole === 'reviewer' || userRole === 'superuser';
    
    return allowEditApproved && hasEditPermission;
  };

  const handleSubmitReview = async () => {
    if (!reviewStatus) {
      addToast({
        type: 'warning',
        title: 'Review Status Required',
        message: 'Please select a review status'
      });
      return;
    }

    if (!audit?.id) {
      addToast({
        type: 'error',
        title: 'Missing Audit ID',
        message: 'Audit ID is missing. Please refresh the page and try again.'
      });
      return;
    }

    try {
      setSubmitting(true);
      console.log('Submitting review with:', {
        audit_id: audit.id,
        review_notes: reviewNotes,
        review_status: reviewStatus
      });
      
      const response = await api.reviews.submitReview({
        audit_id: audit.id,
        review_notes: reviewNotes,
        review_status: reviewStatus
      });

      if (response.success) {
        addToast({
          type: 'success',
          title: 'Review Submitted',
          message: 'Review submitted successfully'
        });
        navigate('/dashboard');
      } else {
        addToast({
          type: 'error',
          title: 'Submission Failed',
          message: response.message || 'Failed to submit review'
        });
      }
    } catch (error) {
      console.error('Failed to submit review:', error);
      addToast({
        type: 'error',
        title: 'Submission Failed',
        message: 'Failed to submit review'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditField = (fieldPath: string, currentValue: string) => {
    setEditingField(fieldPath);
    setEditValue(currentValue);
  };

  const handleArrayFieldUpdate = async (fieldName: string, updatedArray: any[]) => {
    try {
      setSaving(true);
      
      const updateData = {
        audit_id: parseInt(id!),
        [fieldName]: updatedArray
      };

      console.log('Sending array update data:', updateData);

      const response = await api.audit.updateAudit(updateData);
      
      if (response.success) {
        // Update local form data immediately
        setFormData((prev: FormData) => ({
          ...prev,
          [fieldName]: updatedArray
        }));
        console.log(`${fieldName} updated successfully`);
      } else {
        console.error('Failed to update field:', response.message);
        addToast({
          type: 'error',
          title: 'Update Failed',
          message: 'Failed to update field'
        });
      }
    } catch (error) {
      console.error('Failed to update field:', error);
      addToast({
        type: 'error',
        title: 'Update Failed',
        message: 'Failed to update field'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!audit || !editingField) return;

    try {
      setSaving(true);
      
      // Create the update data structure - send individual field
      // Use the audit form ID from the URL parameter, not the assignment ID
      const updateData = {
        audit_id: parseInt(id!),
        [editingField]: editValue
      };

      console.log('Sending update data:', updateData);

      const response = await api.audit.updateAudit(updateData);
      
      if (response.success) {
        // Field updated successfully
        setEditingField(null);
        setEditValue('');
        addToast({
          type: 'success',
          title: 'Field Updated',
          message: 'Field updated successfully'
        });
        // Refresh the audit data to get the updated form data
        fetchAuditData();
      } else {
        addToast({
          type: 'error',
          title: 'Update Failed',
          message: response.message || 'Failed to update field'
        });
      }
    } catch (error) {
      console.error('Failed to update field:', error);
      addToast({
        type: 'error',
        title: 'Update Failed',
        message: 'Failed to update field'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingField(null);
    setEditValue('');
  };

  const handleExportPDF = async () => {
    try {
      if (!id) {
        addToast({
          type: 'error',
          title: 'Export Failed',
          message: 'No audit form ID available for export'
        });
        return;
      }
      
      const response = await api.pdfExport.exportAuditForm(parseInt(id));
      if (response.success) {
        // Success feedback is handled by the API
        console.log('PDF export successful');
        addToast({
          type: 'success',
          title: 'PDF Exported',
          message: 'PDF export completed successfully'
        });
      } else {
        addToast({
          type: 'error',
          title: 'Export Failed',
          message: `Failed to export PDF: ${response.message}`
        });
      }
    } catch (error) {
      console.error('PDF export error:', error);
      addToast({
        type: 'error',
        title: 'Export Failed',
        message: 'Failed to export PDF. Please try again.'
      });
    }
  };

  const renderQuestionSection = (title: string, questions: any[], fieldName: string) => {
    if (!questions || questions.length === 0) return null;

    return (
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">{title}</h3>
        </div>
        
        <div className="space-y-4">
          {questions.map((question, index) => (
            <div key={question.id || index} className="border rounded-lg p-4 group relative">
              <div className="mb-3">
                <label className="form-label font-medium">{question.question}</label>
                <div className="mt-2">
                  {editMode ? (
                    <select
                      value={question.answer || ''}
                      onChange={(e) => {
                        const newQuestions = [...questions];
                        newQuestions[index] = { ...question, answer: e.target.value };
                        // Update the form data immediately for better UX
                        setFormData((prev: FormData) => ({
                          ...prev,
                          [fieldName]: newQuestions
                        }));
                        // Save to backend
                        handleArrayFieldUpdate(fieldName, newQuestions);
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="">Select Answer</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                      <option value="NA">N/A</option>
                    </select>
                  ) : (
                    <span className={`badge ${
                      question.answer === 'Yes' ? 'badge-success' :
                      question.answer === 'No' ? 'badge-danger' :
                      'badge-info'
                    }`}>
                      {question.answer || 'Not answered'}
                    </span>
                  )}
                </div>
              </div>
              
              <div>
                <label className="form-label">Comments</label>
                {editMode ? (
                  <textarea
                    value={question.comment || ''}
                    onChange={(e) => {
                      const newQuestions = [...questions];
                      newQuestions[index] = { ...question, comment: e.target.value };
                      // Update the form data immediately for better UX
                      setFormData((prev: FormData) => ({
                        ...prev,
                        [fieldName]: newQuestions
                      }));
                      // Save to backend
                      handleArrayFieldUpdate(fieldName, newQuestions);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    rows={3}
                    placeholder="Add comments..."
                  />
                ) : (
                  <div className="bg-gray-50 p-3 rounded border">
                    <p className="text-sm">{question.comment || 'No comments'}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderRecommendations = (recommendations: any[]) => {
    if (!recommendations || recommendations.length === 0) return null;

    return (
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Recommendations & Action Items</h3>
        </div>
        
        <div className="space-y-4">
          {recommendations.map((rec, index) => (
            <div key={index} className="border rounded-lg p-4 group relative">
              <div className="flex justify-between items-start mb-3">
                <h4 className="font-medium">Recommendation {index + 1}</h4>
                <div className="flex items-center space-x-2">
                  <span className="badge badge-warning">
                    Pending
                  </span>
                </div>
              </div>
              
              <div className="space-y-3">
                <div>
                  <label className="form-label">Recommendation Title</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={rec.title || ''}
                      onChange={(e) => {
                        const newRecommendations = [...recommendations];
                        newRecommendations[index] = { ...rec, title: e.target.value };
                        setFormData((prev: FormData) => ({
                          ...prev,
                          recommendations: newRecommendations
                        }));
                        handleArrayFieldUpdate('recommendations', newRecommendations);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Enter recommendation title..."
                    />
                  ) : (
                    <div className="bg-gray-50 p-3 rounded border">
                      <p className="text-sm font-medium">{rec.title || 'No recommendation title'}</p>
                    </div>
                  )}
                </div>
                
                <div>
                  <label className="form-label">Description</label>
                  {editMode ? (
                    <textarea
                      value={rec.description || ''}
                      onChange={(e) => {
                        const newRecommendations = [...recommendations];
                        newRecommendations[index] = { ...rec, description: e.target.value };
                        setFormData((prev: FormData) => ({
                          ...prev,
                          recommendations: newRecommendations
                        }));
                        handleArrayFieldUpdate('recommendations', newRecommendations);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      rows={3}
                      placeholder="Enter recommendation description..."
                    />
                  ) : (
                    <div className="bg-gray-50 p-3 rounded border">
                      <p className="text-sm">{rec.description || 'No description'}</p>
                    </div>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Priority</label>
                    {editMode ? (
                      <select
                        value={rec.priority || 'medium'}
                        onChange={(e) => {
                          const newRecommendations = [...recommendations];
                          newRecommendations[index] = { ...rec, priority: e.target.value };
                          setFormData((prev: FormData) => ({
                            ...prev,
                            recommendations: newRecommendations
                          }));
                          handleArrayFieldUpdate('recommendations', newRecommendations);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                      </select>
                    ) : (
                      <div className="bg-gray-50 p-3 rounded border">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          rec.priority === 'critical' ? 'bg-red-100 text-red-800' :
                          rec.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                          rec.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {rec.priority || 'medium'}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <label className="form-label">Category ID</label>
                    <div className="bg-gray-50 p-3 rounded border">
                      <p className="text-sm">{rec.category_id || 'Not specified'}</p>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="form-label">Action Plan</label>
                    {editMode ? (
                      <textarea
                        value={rec.action_plan || ''}
                        onChange={(e) => {
                          const newRecommendations = [...recommendations];
                          newRecommendations[index] = { ...rec, action_plan: e.target.value };
                          setFormData((prev: FormData) => ({
                            ...prev,
                            recommendations: newRecommendations
                          }));
                          handleArrayFieldUpdate('recommendations', newRecommendations);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        rows={2}
                        placeholder="Enter action plan..."
                      />
                    ) : (
                      <div className="bg-gray-50 p-3 rounded border">
                        <p className="text-sm">{rec.action_plan || 'No action plan'}</p>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <label className="form-label">Lead Person</label>
                    {editMode ? (
                      <input
                        type="text"
                        value={rec.lead_person || ''}
                        onChange={(e) => {
                          const newRecommendations = [...recommendations];
                          newRecommendations[index] = { ...rec, lead_person: e.target.value };
                          setFormData((prev: FormData) => ({
                            ...prev,
                            recommendations: newRecommendations
                          }));
                          handleArrayFieldUpdate('recommendations', newRecommendations);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="Enter lead person..."
                      />
                    ) : (
                      <div className="bg-gray-50 p-3 rounded border">
                        <p className="text-sm">{rec.lead_person || 'Not assigned'}</p>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <label className="form-label">Follow-up Date</label>
                    {editMode ? (
                      <input
                        type="date"
                        value={rec.due_date || ''}
                        onChange={(e) => {
                          const newRecommendations = [...recommendations];
                          newRecommendations[index] = { ...rec, due_date: e.target.value };
                          setFormData((prev: FormData) => ({
                            ...prev,
                            recommendations: newRecommendations
                          }));
                          handleArrayFieldUpdate('recommendations', newRecommendations);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    ) : (
                      <div className="bg-gray-50 p-3 rounded border">
                        <p className="text-sm">{rec.due_date ? new Date(rec.due_date).toLocaleDateString() : 'Not set'}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderAuditTeam = (team: any[]) => {
    if (!team || team.length === 0) return null;

    return (
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Audit Team & Signatures</h3>
        </div>
        
        <div className="space-y-4">
          {team.map((member, index) => (
            <div key={index} className="border rounded-lg p-4 group relative">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="form-label">Name</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={member.name || ''}
                      onChange={(e) => {
                        const newTeam = [...team];
                        newTeam[index] = { ...member, name: e.target.value };
                        setFormData((prev: FormData) => ({
                          ...prev,
                          audit_team: newTeam
                        }));
                        handleArrayFieldUpdate('audit_team', newTeam);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Enter name..."
                    />
                  ) : (
                    <p className="font-medium">{member.name || 'Not provided'}</p>
                  )}
                </div>
                
                <div>
                  <label className="form-label">Role/Title</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={member.role || ''}
                      onChange={(e) => {
                        const newTeam = [...team];
                        newTeam[index] = { ...member, role: e.target.value };
                        setFormData((prev: FormData) => ({
                          ...prev,
                          audit_team: newTeam
                        }));
                        handleArrayFieldUpdate('audit_team', newTeam);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Enter role..."
                    />
                  ) : (
                    <p className="font-medium">{member.role || 'Not provided'}</p>
                  )}
                </div>
                
                <div>
                  <label className="form-label">Department</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={member.department || ''}
                      onChange={(e) => {
                        const newTeam = [...team];
                        newTeam[index] = { ...member, department: e.target.value };
                        setFormData((prev: FormData) => ({
                          ...prev,
                          audit_team: newTeam
                        }));
                        handleArrayFieldUpdate('audit_team', newTeam);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Enter department..."
                    />
                  ) : (
                    <p className="font-medium">{member.department || 'Not provided'}</p>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Electronic Signature</label>
                  {editMode ? (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={member.signature || ''}
                        onChange={(e) => {
                          const newTeam = [...team];
                          newTeam[index] = { ...member, signature: e.target.value };
                          setFormData((prev: FormData) => ({
                            ...prev,
                            audit_team: newTeam
                          }));
                          handleArrayFieldUpdate('audit_team', newTeam);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="Enter signature data URL..."
                      />
                      {member.signature && (
                        <div className="border rounded p-2">
                          <img src={member.signature} alt="Signature" className="max-w-full h-20 object-contain" />
                        </div>
                      )}
                    </div>
                  ) : (
                    member.signature ? (
                      <div className="border rounded p-2">
                        <img src={member.signature} alt="Signature" className="max-w-full h-20 object-contain" />
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No signature provided</p>
                    )
                  )}
                </div>
                
                <div>
                  <label className="form-label">Date & Time</label>
                  {editMode ? (
                    <input
                      type="datetime-local"
                      value={member.signature_date ? new Date(member.signature_date).toISOString().slice(0, 16) : ''}
                      onChange={(e) => {
                        const newTeam = [...team];
                        newTeam[index] = { ...member, signature_date: e.target.value };
                        setFormData((prev: FormData) => ({
                          ...prev,
                          audit_team: newTeam
                        }));
                        handleArrayFieldUpdate('audit_team', newTeam);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="font-medium">
                      {member.signature_date ? new Date(member.signature_date).toLocaleString() : 'Not provided'}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Unable to Load Audit
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => navigate('/reviews')}
                  className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Back to Reviews
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!audit) {
    return (
      <div className="container">
        <div className="alert alert-error">
          <h2>Audit Not Found</h2>
          <p>The requested audit could not be found.</p>
        </div>
      </div>
    );
  }

  // Debug: Log current state
  console.log('SupervisorReview render - editMode:', editMode, 'formData:', formData);

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Edit Mode Banner */}
      {editMode && (
        <div className="bg-amber-100 border-l-4 border-amber-500 p-4 rounded-lg">
          <div className="flex items-center">
            <Pencil className="w-5 h-5 text-amber-600 mr-2" />
            <p className="text-amber-800 font-medium">Edit Mode Active - Click the pencil icons to edit fields</p>
          </div>
        </div>
      )}
      
      {/* Approved Audit Banner */}
      {audit?.status === 'approved' && (
        <div className={`border-l-4 p-4 rounded-lg ${
          canEditApprovedAudit() 
            ? 'bg-blue-100 border-blue-500' 
            : 'bg-green-100 border-green-500'
        }`}>
          <div className="flex items-center">
            <CheckCircle className={`w-5 h-5 mr-2 ${
              canEditApprovedAudit() 
                ? 'text-blue-600' 
                : 'text-green-600'
            }`} />
            <p className={`font-medium ${
              canEditApprovedAudit() 
                ? 'text-blue-800' 
                : 'text-green-800'
            }`}>
              {canEditApprovedAudit() 
                ? 'This audit has been approved but can be edited by reviewers and superusers'
                : 'This audit has been approved and is now read-only'
              }
            </p>
          </div>
        </div>
      )}
      
      {/* Enhanced Header */}
      <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                <ClipboardCheck className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Supervisor Review</h1>
                <p className="text-emerald-100">Comprehensive audit review and approval process</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => {
                  if (!canEditApprovedAudit()) return;
                  console.log('Edit mode clicked, current state:', editMode);
                  setEditMode(!editMode);
                  console.log('Edit mode new state:', !editMode);
                }}
                disabled={!canEditApprovedAudit()}
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-all duration-200 backdrop-blur-sm ${
                  !canEditApprovedAudit()
                    ? 'bg-gray-400 bg-opacity-50 text-gray-600 cursor-not-allowed'
                    : editMode 
                      ? 'bg-white bg-opacity-30 text-white' 
                      : 'bg-white bg-opacity-20 text-white hover:bg-opacity-30'
                }`}
                title={!canEditApprovedAudit() ? 'Cannot edit approved audits' : ''}
              >
                <Pencil className="w-4 h-4" />
                <span>{editMode ? 'Exit Edit Mode' : 'Edit Mode'}</span>
              </button>
              <button
                onClick={() => navigate('/reviews')}
                className="flex items-center space-x-2 px-4 py-2 bg-white bg-opacity-20 rounded-xl text-white hover:bg-opacity-30 transition-all duration-200 backdrop-blur-sm"
              >
                <FileText className="w-4 h-4" />
                <span>Back to Reviews</span>
              </button>
            </div>
          </div>
        </div>
        
        {patientData && (
          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 border border-blue-200">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 bg-blue-200 rounded-lg flex items-center justify-center">
                    <User className="w-4 h-4 text-blue-700" />
                  </div>
                  <label className="text-sm font-semibold text-blue-700">Patient ID</label>
                </div>
                <p className="text-lg font-bold text-blue-900">{patientData.patient_id}</p>
              </div>
              
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 border border-green-200">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 bg-green-200 rounded-lg flex items-center justify-center">
                    <User className="w-4 h-4 text-green-700" />
                  </div>
                  <label className="text-sm font-semibold text-green-700">Auditor</label>
                </div>
                <p className="text-lg font-bold text-green-900">
                  {audit?.auditor_name || 'Loading...'}
                </p>
              </div>
              
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-6 border border-purple-200">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 bg-purple-200 rounded-lg flex items-center justify-center">
                    <Calendar className="w-4 h-4 text-purple-700" />
                  </div>
                  <label className="text-sm font-semibold text-purple-700">Assigned Date</label>
                </div>
                <p className="text-lg font-bold text-purple-900">{new Date(audit.assigned_date).toLocaleDateString()}</p>
              </div>
              
              <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-2xl p-6 border border-indigo-200">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 bg-indigo-200 rounded-lg flex items-center justify-center">
                    <Calendar className="w-4 h-4 text-indigo-700" />
                  </div>
                  <label className="text-sm font-semibold text-indigo-700">Due Date</label>
                </div>
                <p className="text-lg font-bold text-indigo-900">{new Date(audit.due_date).toLocaleDateString()}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Case Information */}
      {formData && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Case Information</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="form-label">Patient IP Number</label>
              <p className="font-medium">{formData.case_information?.patient_ip_number || 'Not provided'}</p>
            </div>
            
            <div>
              <label className="form-label">Age</label>
              <p className="font-medium">{formData.case_information?.age || 'Not provided'}</p>
            </div>
            
            <div>
              <label className="form-label">Admission Date</label>
              <p className="font-medium">
                {formData.case_information?.admission_date ? 
                  new Date(formData.case_information.admission_date).toLocaleString() : 
                  'Not provided'
                }
              </p>
            </div>
            
            <div>
              <label className="form-label">Discharge Date</label>
              <p className="font-medium">
                {formData.case_information?.discharge_date ? 
                  new Date(formData.case_information.discharge_date).toLocaleString() : 
                  'Not provided'
                }
              </p>
            </div>
            
            <div>
              <label className="form-label">Length of Stay</label>
              <p className="font-medium">{formData.case_information?.length_of_stay || 'Not provided'} days</p>
            </div>
            
            <div>
              <label className="form-label">Source of Admission</label>
              <p className="font-medium">{formData.case_information?.source_of_admission || 'Not provided'}</p>
            </div>
            
            <div className="md:col-span-2">
              <label className="form-label">Primary Diagnosis</label>
              {editingField === 'primary_diagnosis' ? (
                <div className="flex items-center space-x-2">
                  <textarea
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    rows={3}
                  />
                  <button
                    onClick={handleSaveEdit}
                    disabled={saving}
                    className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition-colors"
                    title="Save"
                  >
                    {saving ? (
                      <div className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                    title="Cancel"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="bg-gray-50 p-3 rounded border group relative">
                  <p className="text-sm">{formData.case_information?.primary_diagnosis || 'Not provided'}</p>
                  {editMode && (
                    <button
                      onClick={() => {
                        console.log('Edit button clicked for primary_diagnosis');
                        handleEditField('primary_diagnosis', formData.case_information?.primary_diagnosis || '');
                      }}
                      className="absolute top-2 right-2 p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded opacity-100 transition-all duration-200"
                      title="Edit"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                  )}
                </div>
              )}
            </div>
            
            <div className="md:col-span-2">
              <label className="form-label">Cause of Death</label>
              {editingField === 'cause_of_death' ? (
                <div className="flex items-center space-x-2">
                  <textarea
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    rows={3}
                  />
                  <button
                    onClick={handleSaveEdit}
                    disabled={saving}
                    className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition-colors"
                    title="Save"
                  >
                    {saving ? (
                      <div className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                    title="Cancel"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="bg-gray-50 p-3 rounded border group relative">
                  <p className="text-sm">{formData.case_information?.cause_of_death || 'Not provided'}</p>
                  {editMode && (
                    <button
                      onClick={() => {
                        console.log('Edit button clicked for cause_of_death');
                        handleEditField('cause_of_death', formData.case_information?.cause_of_death || '');
                      }}
                      className="absolute top-2 right-2 p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded opacity-100 transition-all duration-200"
                      title="Edit"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Case Summary */}
      {formData.case_information?.case_summary && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Case Summary</h3>
          </div>
          
          {editMode ? (
            <div className="p-4">
              <textarea
                value={formData.case_information?.case_summary || ''}
                onChange={(e) => {
                  setFormData((prev: FormData) => ({
                    ...prev,
                    case_information: {
                      ...prev.case_information,
                      case_summary: e.target.value
                    }
                  }));
                }}
                onBlur={() => {
                  if (audit?.id) {
                    handleArrayFieldUpdate('case_summary', [formData.case_information?.case_summary]);
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                rows={4}
                placeholder="Provide a comprehensive case summary..."
              />
            </div>
          ) : (
            <div className="bg-gray-50 p-4 rounded border">
              <p className="text-sm whitespace-pre-wrap">{formData.case_information?.case_summary}</p>
            </div>
          )}
        </div>
      )}

      {/* Structured Questions */}
      {renderQuestionSection('Documentation Adequacy', formData.documentation_questions || [], 'documentation_questions')}
      {renderQuestionSection('Diagnosis Adequacy', formData.diagnosis_questions || [], 'diagnosis_questions')}
      {renderQuestionSection('Management Process', formData.management_questions || [], 'management_questions')}
      {renderQuestionSection('Outcome Contributors', formData.outcome_questions || [], 'outcome_questions')}
      {renderQuestionSection('Disclosure Section', formData.disclosure_questions || [], 'disclosure_questions')}
      {renderQuestionSection('Second Victim Support', formData.second_victim_questions || [], 'second_victim_questions')}

      {/* Issues Revealed */}
      {formData.issues_revealed && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Issues Revealed</h3>
          </div>
          
          {editMode ? (
            <div className="p-4">
              <textarea
                value={formData.issues_revealed || ''}
                onChange={(e) => {
                  setFormData((prev: FormData) => ({
                    ...prev,
                    issues_revealed: e.target.value
                  }));
                }}
                onBlur={() => {
                  if (audit?.id) {
                    handleArrayFieldUpdate('issues_revealed', [formData.issues_revealed]);
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                rows={4}
                placeholder="Describe any issues revealed during the audit..."
              />
            </div>
          ) : (
            <div className="bg-gray-50 p-4 rounded border">
              <p className="text-sm whitespace-pre-wrap">{formData.issues_revealed}</p>
            </div>
          )}
        </div>
      )}

      {/* Recommendations */}
      {renderRecommendations(formData.recommendations || [])}

      {/* Audit Team */}
      {renderAuditTeam(formData.audit_team || [])}

      {/* Review Section */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
            <Award className="w-5 h-5 text-indigo-600" />
            <span>Supervisor Review & Decision</span>
          </h3>
        </div>
        
        <div className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Review Decision</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className={`relative flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 ${
                reviewStatus === 'approved' 
                  ? 'border-green-500 bg-green-50 shadow-md' 
                  : 'border-gray-200 hover:border-green-300 hover:bg-green-25'
              }`}>
                <input
                  type="radio"
                  value="approved"
                  checked={reviewStatus === 'approved'}
                  onChange={(e) => setReviewStatus(e.target.value)}
                  className="sr-only"
                />
                <div className="flex items-center space-x-3">
                  <CheckCircle className={`h-6 w-6 ${reviewStatus === 'approved' ? 'text-green-600' : 'text-gray-400'}`} />
                  <div>
                    <div className="font-medium text-gray-900">Approve</div>
                    <div className="text-sm text-gray-500">Audit meets standards</div>
                  </div>
                </div>
              </label>

              <label className={`relative flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 ${
                reviewStatus === 'rejected' 
                  ? 'border-red-500 bg-red-50 shadow-md' 
                  : 'border-gray-200 hover:border-red-300 hover:bg-red-25'
              }`}>
                <input
                  type="radio"
                  value="rejected"
                  checked={reviewStatus === 'rejected'}
                  onChange={(e) => setReviewStatus(e.target.value)}
                  className="sr-only"
                />
                <div className="flex items-center space-x-3">
                  <XCircle className={`h-6 w-6 ${reviewStatus === 'rejected' ? 'text-red-600' : 'text-gray-400'}`} />
                  <div>
                    <div className="font-medium text-gray-900">Reject</div>
                    <div className="text-sm text-gray-500">Significant issues found</div>
                  </div>
                </div>
              </label>

              <label className={`relative flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 ${
                reviewStatus === 'needs_revision' 
                  ? 'border-yellow-500 bg-yellow-50 shadow-md' 
                  : 'border-gray-200 hover:border-yellow-300 hover:bg-yellow-25'
              }`}>
                <input
                  type="radio"
                  value="needs_revision"
                  checked={reviewStatus === 'needs_revision'}
                  onChange={(e) => setReviewStatus(e.target.value)}
                  className="sr-only"
                />
                <div className="flex items-center space-x-3">
                  <Edit className={`h-6 w-6 ${reviewStatus === 'needs_revision' ? 'text-yellow-600' : 'text-gray-400'}`} />
                  <div>
                    <div className="font-medium text-gray-900">Needs Revision</div>
                    <div className="text-sm text-gray-500">Minor improvements required</div>
                  </div>
                </div>
              </label>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Review Notes & Feedback
            </label>
            <textarea
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
              rows={6}
              placeholder="Provide detailed feedback, recommendations, and any specific areas that need attention..."
            />
            <div className="mt-2 text-sm text-gray-500">
              {reviewNotes.length}/1000 characters
            </div>
          </div>
          
          <div className="flex justify-between items-center pt-4 border-t border-gray-100">
            <button
              onClick={() => navigate('/reviews')}
              className="flex items-center space-x-2 px-6 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-all duration-200"
            >
              <FileText className="w-4 h-4" />
              <span>Back to Reviews</span>
            </button>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="px-6 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-all duration-200"
              >
                Cancel
              </button>
              
              {/* PDF Export Button - Only show for approved audits */}
              {audit?.status === 'approved' && (
                <button
                  onClick={handleExportPDF}
                  className="flex items-center space-x-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  <Download className="w-4 h-4" />
                  <span>Export PDF</span>
                </button>
              )}
              
              <button
                onClick={handleSubmitReview}
                disabled={submitting || !reviewStatus}
                className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                  submitting || !reviewStatus
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 shadow-lg hover:shadow-xl'
                }`}
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>Submit Review</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

